import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../forms/fields/checkbox/ziro-checkbox.component';
import { TranslateService } from '@ngx-translate/core';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../button/button.component';
import {
  LanguageOption,
  ResourceAccountEntityArrayName,
  ResourceAccountViewArrayName,
  UiCallQueue,
} from '../teams-call-queue-edit.component';
import { Popover } from 'primeng/popover';
import {
  AccountType,
  CallbackSettings,
  GreetingType,
  Microsoft360View,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsCallQueueAudioSettings,
  MicrosoftTeamsCallQueueView,
  MicrosoftTeamsCallQueueViewEntityStatus,
  MicrosoftTeamsUserResult,
  MusicOnHoldType,
  NoAgentApplyTo,
  RoutingMethod,
  VoiceAppViewEntity,
  VoiceAppViewEntityType,
} from '../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ZiroModalPromptModalOptions } from '../../../modals/prompt-modal/prompt-modal.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, take } from 'rxjs/operators';
import { getUserInitialsFromDisplayName } from '../../../shared/utils';
import { cloneDeep, isEqual, omit } from 'lodash';
import { AudioFormComponent } from './audio-form/audio-form.component';
import { CallbackSettingsFormComponent } from './callback-settings-form/callback-settings-form.component';
import { CollapsibleSectionCardSwitchValue } from '../../../shared/collapsible-section-card/collapsible-section-card.component';
import {
  AudioFileResource,
  AudioPostResponse,
  UiAudioPostResponse,
} from '../../../shared/resources/audio-file.resource';
import { Microsoft360ViewContext } from '../../../shared/contexts/microsoft-360-view.context';
import { TeamsCallQueuesContext } from '../../teams-call-queues.context';
import { TeamsCallQueueTeamsUsersSearchResource } from '../../teams-call-queue-teams-users-search.resource';
import {
  AgentMode,
  AgentsCardComponent,
  TeamsCallQueueType,
  UiAgentSelection,
} from './agents-card/agents-card.component';
import {
  CallQueueExceptionHandlingFormComponent,
  ExceptionHandlingPromptType,
  ExceptionHandlingSectionType,
  mapUiToExceptionHandling,
  UiExceptionHandlingSettings,
} from './exception-handling-form/call-queue-exception-handling-form.component';

export interface UiAudioSettings extends MicrosoftTeamsCallQueueAudioSettings {
  greetingUpload?: File;
  musicOnHoldUpload?: File;
}

export enum OfferPromptType {
  AUDIO_FILE = 'AUDIO_FILE',
  TTS_MESSAGE = 'TTS_MESSAGE',
}

export interface UiCallbackSettings extends CallbackSettings {
  offerPromptType?: OfferPromptType;
  offerAudioUpload?: File;
  notificationTargetGroup?: ZiroSelectOption;
}

@Component({
  selector: 'ziro-teams-call-queue-edit-form',
  templateUrl: './teams-call-queue-edit-form.component.html',
  styleUrl: './teams-call-queue-edit-form.component.scss',
  standalone: false,
})
export class TeamsCallQueueEditFormComponent
  extends ZiroFormAbstractDirective<UiCallQueue>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _translateService = inject(TranslateService);
  private _renderer2 = inject(Renderer2);
  private _msTeamsCallQueueTeamsUserSearchResource = inject(TeamsCallQueueTeamsUsersSearchResource);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _callQueueContext = inject(TeamsCallQueuesContext);
  private _audioFileResource = inject(AudioFileResource);
  private _ms360ViewContext: Microsoft360ViewContext;

  audioFormComponent = viewChild(AudioFormComponent);
  callbackSettingsFormComponent = viewChild(CallbackSettingsFormComponent);

  overflowFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('overflowForm');
  timeoutFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('timeoutForm');
  noAgentsFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('noAgentsForm');

  activeExceptionTab = ExceptionHandlingSectionType.OVERFLOW;
  exceptionTabsValidationState = {
    [ExceptionHandlingSectionType.OVERFLOW]: signal(ZiroFormsValidationState.VALID),
    [ExceptionHandlingSectionType.TIMEOUT]: signal(ZiroFormsValidationState.VALID),
    [ExceptionHandlingSectionType.NO_AGENTS]: signal(ZiroFormsValidationState.VALID),
  };
  private _exceptionTabBySettingsKey = {
    uiOverflowSettings: ExceptionHandlingSectionType.OVERFLOW,
    uiTimeoutSettings: ExceptionHandlingSectionType.TIMEOUT,
    uiNoAgentSettings: ExceptionHandlingSectionType.NO_AGENTS,
  } as const;

  isQueueValid = output<boolean>();

  resourceAccountsButton = viewChild<ElementRef<HTMLDivElement>>('resourceButton');
  resourceAccountsPopoverContainer = viewChild<ElementRef<HTMLDivElement>>('resourceAccountsPopoverContainer');
  resourceAccountsPopover = viewChild<Popover>('resourceAccountsPopover');

  callingIdResourceAccountsButton = viewChild<ElementRef<HTMLDivElement>>('callingIdButton');
  callingIdResourceAccountsPopoverContainer = viewChild<ElementRef<HTMLDivElement>>(
    'callingIdResourceAccountsPopoverContainer',
  );
  callingIdResourceAccountsPopover = viewChild<Popover>('callingIdResourceAccountsPopover');

  agentsCard = viewChild(AgentsCardComponent);

  allTenantCallQueues = signal<MicrosoftTeamsCallQueue[]>([]);

  callQueueViewSource = input<MicrosoftTeamsCallQueueView>();
  callQueueView = computed(() => signal(this.callQueueViewSource()));

  existingQueueResourceAccounts = computed(() =>
    this.callQueueView()().resourceAccounts.map((account) => account.guid),
  );
  existingQueueCallingIdResourceAccounts = computed(() =>
    this.callQueueView()().callingIdResourceAccounts.map((account) => account.guid),
  );

  private _otherQueuesResourceAccountGuids = signal<string[]>([]);
  nonSelectableResourceAccountGuids = computed(() => signal(this._otherQueuesResourceAccountGuids()));
  nonSelectableCallingIdResourceAccountGuids = computed(() => signal(this._otherQueuesResourceAccountGuids()));

  languageOptions = input<LanguageOption[]>();

  is360ViewChild = false;
  resourceAccountType = TeamsCallQueueType.RESOURCE_ACCOUNT;

  private _term: string;
  private _teamsUserCandidates: MicrosoftTeamsUserResult[];
  private _subs = new Subscription();
  private _audioSettings: UiAudioSettings;
  private _greetingAudioChanged = false;
  private _musicOnHoldAudioChanged = false;
  private _initialAudioSettings: UiAudioSettings;
  private _initialExceptionSettings: Pick<
    UiCallQueue,
    'uiOverflowSettings' | 'uiTimeoutSettings' | 'uiNoAgentSettings'
  >;
  private _callbackSettings: UiCallbackSettings;
  private _offerAudioChanged = false;
  private _initialCallbackSettings: UiCallbackSettings;

  private _upn: string;
  private _tenantId: number;
  private _callQueueId: string;
  private _returnUrl: string;

  constructor() {
    super();
    this.is360ViewChild = this._route.snapshot.data['is360ViewChild'];
    this._tenantId = this._route.snapshot.params.tenantId;

    if (this.is360ViewChild) {
      this._ms360ViewContext = inject(Microsoft360ViewContext);
    }

    effect(() => {
      this._setPopoverWidth(this.resourceAccountsButton(), this.resourceAccountsPopoverContainer());
    });

    effect(() => {
      this._setPopoverWidth(this.callingIdResourceAccountsButton(), this.callingIdResourceAccountsPopoverContainer());
    });
  }

  getUserInitialsFromDisplayName = getUserInitialsFromDisplayName;
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  formConfig: ZiroFormConfig;
  isSearching = false;
  isAudioLoading: boolean;
  tooltipStyles: Record<string, string>;

  ngOnInit(): void {
    this._returnUrl = this._route.snapshot.queryParamMap.get('returnUrl');
    if (this._returnUrl) {
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { returnUrl: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this._upn = this._route.snapshot.params.upn;
    this._initialAudioSettings = cloneDeep(this.entity.audioSettings);
    this._initialExceptionSettings = cloneDeep({
      uiOverflowSettings: this.entity.uiOverflowSettings,
      uiTimeoutSettings: this.entity.uiTimeoutSettings,
      uiNoAgentSettings: this.entity.uiNoAgentSettings,
    });
    this._initialCallbackSettings = cloneDeep(this.entity.callbackSettings);
    this._callQueueId = this._route.snapshot.params['callQueueId'];

    const callQueueId = this._route.snapshot.params.callQueueId;

    this._callQueueContext.state$.pipe(take(1)).subscribe((allCallQueues) => {
      this.allTenantCallQueues.set(allCallQueues);
      const otherQueuesResourceAccountGuids = allCallQueues
        .filter((queue) => queue.callQueueId !== callQueueId)
        .map((callQueue) => [...callQueue.resourceAccountGuids, ...callQueue.callingIdResourceAccountGuids])
        .flat();
      this._otherQueuesResourceAccountGuids.set(otherQueuesResourceAccountGuids);
    });

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<MicrosoftTeamsCallQueue>) => {
      this.isQueueValid.emit(this._checkQueueValidity());

      if (update.old.conferenceMode && !update.new.conferenceMode && update.old.callbackSettings?.enabled) {
        this._confirmDisableCallbacksOnConferenceModeOff();
      }

      if (!this.is360ViewChild) {
        this._breadcrumbsService.updateBreadcrumbs([
          {
            label: 'tkey;teams_call_queues.list_view.bread_crumb',
            url: `/call-queue-management/tenants/${this.callQueueViewSource().tenantId}/teams-call-queues`,
            routerLink: true,
          },
          {
            label: update.new.name,
          },
        ]);
      } else {
        this._breadcrumbsService.updateBreadcrumbs([
          {
            label: 'tkey;microsoft_360.view.breadcrumb.home.text',
            url: `/helpdesk/tenants/${this._tenantId}/360-view/${this._upn}`,
            routerLink: true,
          },
          {
            label: 'tkey;microsoft_360.view.call_queues.breadcrumb.text',
          },
          {
            label: update.new.name,
          },
        ]);
      }
    });

    this._subs.add(formUpdateSub);

    this.formConfig = {
      fields: {
        name: {
          dataAutomation: 'call-queue-name',
          label: 'tkey;teams_call_queue.settings.name.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
          required: () => true,
          validation: [
            {
              validator: (val) => (val.length > 64 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID),
              message: this._translateService.instant('tkey;validators.global.error.maxlength', { maxlength: 64 }),
            },
            {
              validator: (val: string) => {
                return this.allTenantCallQueues()
                  .filter((callQueue) =>
                    this.callQueueViewSource().callQueueId
                      ? this.callQueueViewSource().callQueueId !== callQueue.callQueueId
                      : true,
                  )
                  .some((callQueue) => callQueue.name.toLowerCase() === val.toLowerCase())
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;teams_call_queue.settings.name.unique.validation',
            },
          ],
        },
        language: {
          dataAutomation: 'call-queue-language',
          label: 'tkey;teams_call_queue.settings.language.label',
          componentConfig: new ZiroSelectConfig({
            options: this.languageOptions(),
            placeholder: 'tkey;teams_call_queue.settings.language.placeholder',
            bindValue: 'value',
          }),
          required: () => true,
        },
        routingMethod: {
          dataAutomation: 'call-queue-routing-method',
          label: 'tkey;teams_call_queue.settings.routing_method.label',
          componentConfig: new ZiroSelectConfig({
            options: [
              {
                label: `<i class="${ZiroIcons.ATTENDANT_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.attendant.title',
                )}`,
                value: RoutingMethod.ATTENDANT,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.attendant.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.SERIAL_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.serial.title',
                )}`,
                value: RoutingMethod.SERIAL,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.serial.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.ROUND_ROBIN_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.round_robin.title',
                )}`,
                value: RoutingMethod.ROUND_ROBIN,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.round_robin.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.LONGEST_IDLE_TIME_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.longest_idle_time.title',
                )}`,
                value: RoutingMethod.LONGEST_IDLE,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.longest_idle_time.description',
                ),
              },
            ],
            placeholder: 'tkey;teams_call_queue.settings.routing_method.placeholder',
            bindValue: 'value',
          }),
          required: () => true,
        },
        agentAlertTime: {
          dataAutomation: 'call-queue-agent-alert-time',
          label: 'tkey;teams_call_queue.settings.agent_alert_time.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
          }),
          validation: [
            {
              validator: (val) =>
                Number(val) < 15 || Number(val) > 180
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID,
              message: this._translateService.instant('tkey;validators.global.min_max.error', { min: 15, max: 180 }),
            },
            {
              validator: (val, uiTimeoutSettings: UiExceptionHandlingSettings) =>
                Number(val) > Number(uiTimeoutSettings.timeoutMinutes) * 60 + Number(uiTimeoutSettings.timeoutSeconds)
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID,
              injectValuesFromFields: ['uiTimeoutSettings'],
              message: 'tkey;teams_call_queue.settings.agent_alert_time.call_timeout.validation',
            },
          ],
        },
        uiTimeoutSettings: {
          dataAutomation: 'call-queue-timeout-settings-shadow',
          label: '',
          hidden: () => true,
          valExcluded: () => true,
          canModifyFormState: false,
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
        },
        conferenceMode: {
          dataAutomation: 'call-queue-conference-mode',
          label: 'tkey;teams_call_queue.settings.conference_mode.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          disabled: () => this.agentsCard()?.activeAgentMode === AgentMode.TEAMS_CHANNEL,
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.settings.conference_mode.label.tooltip',
          disabledTooltip: 'tkey;teams_call_queue.settings.conference_mode.channel_mode.disabled.tooltip',
        },
        presenceAwareRouting: {
          dataAutomation: 'call-queue-presence-aware-routing',
          label: 'tkey;teams_call_queue.settings.presence_aware_routing.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          disabled: () => this.formData.routingMethod === RoutingMethod.LONGEST_IDLE,
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.settings.presence_aware_routing.label.tooltip',
          disabledTooltip: 'tkey;teams_call_queue.settings.presence_aware_routing.disabled.tooltip',
        },
        allowOptOut: {
          dataAutomation: 'call-queue-allow-opt-out',
          label: 'tkey;teams_call_queue.settings.allow_opt_out.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.settings.allow_opt_out.label.tooltip',
        },
      },
      options: {
        columnClasses: {
          label: 'col',
          input: 'col-2',
        },
        customClasses: 'ms-auto',
      },
    };

    if (!this._callQueueId) {
      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'cancel-button',
            dataAutomation: 'cancel-button',
            label: 'tkey;global.button.cancel.text',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            cb: () => this._navigateAfterAction(),
          },
          {
            id: 'save-button',
            dataAutomation: 'save-button',
            label: 'tkey;global.button.save.text',
            icon: ZiroIcons.SAVE_SUCCESS,
            buttonClass: ButtonStyles.PRIMARY,
            cb: () => this._onSaveClick(),
          },
        ]),
      );
    } else {
      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'cancel-button',
            dataAutomation: 'cancel-button',
            label: 'tkey;global.button.cancel.text',
            buttonClass: ButtonStyles.DEFAULT,
            cb: () => this._navigateAfterAction(),
          },
          {
            id: 'delete-button',
            dataAutomation: 'delete-button',
            label: 'tkey;global.button.delete.text',
            icon: ZiroIcons.DELETE_SMALL,
            buttonClass: ButtonStyles.DANGER,
            cb: () => this._onDeleteClick(),
          },
          {
            id: 'save-button',
            dataAutomation: 'save-button',
            label: 'tkey;global.button.save.text',
            icon: ZiroIcons.SAVE_SUCCESS,
            buttonClass: ButtonStyles.PRIMARY,
            cb: () => this._onSaveClick(),
          },
        ]),
      );
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  resourceAccountsSearch: (text$: Observable<string>) => Observable<MicrosoftTeamsUserResult[]> = (
    text$: Observable<string>,
  ): Observable<MicrosoftTeamsUserResult[]> =>
    text$.pipe(
      tap((term) => {
        if (term && term.length >= 3) {
          this._term = term;
          this.isSearching = true;
        }
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 3) {
          return of([] as MicrosoftTeamsUserResult[]);
        }

        return this._msTeamsCallQueueTeamsUserSearchResource.search(
          this.callQueueViewSource().tenantId.toString(),
          term.trim(),
          null,
          250,
          AccountType.RESOURCE_ACCOUNT,
        );
      }),
      map((teamsUsers) => {
        const teamsUserCandidates = teamsUsers.filter(
          (user) => !this.existingQueueResourceAccounts().includes(user.guid),
        );
        this._teamsUserCandidates = teamsUserCandidates;

        return teamsUserCandidates.sort(
          (a, b) => Number(this.isResourceAccountDisabled(a)) - Number(this.isResourceAccountDisabled(b)),
        );
      }),
      tap(() => (this.isSearching = false)),
      catchError(() => of([] as MicrosoftTeamsUserResult[])),
    );

  callingIdResourceAccountsSearch: (text$: Observable<string>) => Observable<MicrosoftTeamsUserResult[]> = (
    text$: Observable<string>,
  ): Observable<MicrosoftTeamsUserResult[]> =>
    text$.pipe(
      tap((term) => {
        if (term && term.length >= 3) {
          this._term = term;
          this.isSearching = true;
        }
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 3) {
          return of([] as MicrosoftTeamsUserResult[]);
        }
        return this._msTeamsCallQueueTeamsUserSearchResource.search(
          this.callQueueViewSource().tenantId.toString(),
          term.trim(),
          null,
          250,
          AccountType.RESOURCE_ACCOUNT,
        );
      }),
      map((teamsUsers) => {
        const teamsUserCandidates = teamsUsers.filter(
          (user) => !this.existingQueueCallingIdResourceAccounts().includes(user.guid),
        );
        this._teamsUserCandidates = teamsUserCandidates;

        return teamsUserCandidates.sort(
          (a, b) => Number(this.isResourceAccountDisabled(a)) - Number(this.isResourceAccountDisabled(b)),
        );
      }),
      tap(() => (this.isSearching = false)),
      catchError(() => of([] as MicrosoftTeamsUserResult[])),
    );

  resourceAccountFormatter: (userResult: MicrosoftTeamsUserResult) => string = (userResult) =>
    userResult.userPrincipalName;

  addResourceAccount(
    userResult: MicrosoftTeamsUserResult,
    entityArrayName: ResourceAccountEntityArrayName,
    viewArrayName: ResourceAccountViewArrayName,
  ): void {
    const targetResourceAccount = this._teamsUserCandidates.find(
      (resourceAccountUser) => resourceAccountUser.userPrincipalName === userResult.userPrincipalName,
    );

    const viewResourceAccount = this._buildViewResourceAccount(targetResourceAccount);

    this.entitySource.next({
      ...this.entity,
      [entityArrayName]: [...this.entity[entityArrayName], targetResourceAccount.guid],
    });

    this.callQueueView().update((prevView) => ({
      ...prevView,
      [viewArrayName]: [...prevView[viewArrayName], viewResourceAccount],
    }));

    if (entityArrayName === 'resourceAccountGuids') {
      this.nonSelectableResourceAccountGuids().update((prevResourceAccounts) => [
        ...prevResourceAccounts,
        userResult.guid,
      ]);
      this.resourceAccountsPopover().hide();
    } else {
      this.nonSelectableCallingIdResourceAccountGuids().update((prevResourceAccounts) => [
        ...prevResourceAccounts,
        userResult.guid,
      ]);
      this.callingIdResourceAccountsPopover().hide();
    }
  }

  deleteResourceAccount(
    resourceAccount: VoiceAppViewEntity,
    entityArrayName: ResourceAccountEntityArrayName,
    viewArrayName: ResourceAccountViewArrayName,
  ): void {
    this.entitySource.next({
      ...this.entity,
      [entityArrayName]: this.entity[entityArrayName].filter(
        (existingResourceAccountGuid: string) => existingResourceAccountGuid !== resourceAccount.guid,
      ),
    });

    this.callQueueView().update((prevView) => ({
      ...prevView,
      [viewArrayName]: this.callQueueView()()[viewArrayName].filter(
        (existingResourceAccount) => existingResourceAccount.guid !== resourceAccount.guid,
      ),
    }));

    if (entityArrayName === 'resourceAccountGuids') {
      this.nonSelectableResourceAccountGuids().update((guids) => guids.filter((guid) => guid !== resourceAccount.guid));
    } else {
      this.nonSelectableCallingIdResourceAccountGuids().update((guids) =>
        guids.filter((guid) => guid !== resourceAccount.guid),
      );
    }
  }

  setAgentSelection(selection: UiAgentSelection): void {
    const isChannelMode = selection.mode === AgentMode.TEAMS_CHANNEL;

    this.entity = {
      ...this.entity,
      userGuids: selection.userGuids,
      groupGuids: selection.groupGuids,
      microsoftTeamsTeamsId: selection.microsoftTeamsTeamsId,
      teamSchedulingGroupId: selection.teamSchedulingGroupId,
      microsoftTeamsChannelId: selection.microsoftTeamsChannelId,
      conferenceMode: isChannelMode ? true : this.entity.conferenceMode,
    };

    this.entitySource.next(this.entity);
    this._setFieldStates();
    this.isQueueValid.emit(this._checkQueueValidity());
  }

  onRaPopoverOpen(): void {
    (document.querySelector('[data-automation="ra-type-ahead"]') as HTMLInputElement).focus();
  }

  onCallingIdPopoverOpen(): void {
    (document.querySelector('[data-automation="calling-id-type-ahead"]') as HTMLInputElement).focus();
  }

  onPopoverClose(inputEl: HTMLInputElement): void {
    inputEl.value = '';
  }

  setExceptionHandlingSettings(
    update: ZiroFormsUpdate<UiExceptionHandlingSettings>,
    settingsKey: 'uiOverflowSettings' | 'uiTimeoutSettings' | 'uiNoAgentSettings',
  ): void {
    if (update.valid === ZiroFormsValidationState.VALID || update.valid === ZiroFormsValidationState.INVALID) {
      this.exceptionTabsValidationState[this._exceptionTabBySettingsKey[settingsKey]].set(update.valid);
    }

    if (!isEqual(update.new, update.old)) {
      this.entitySource.next({
        ...this.entity,
        [settingsKey]: update.new,
      });
    }
  }

  getExceptionHandlingSubtext(): string {
    if (this.activeExceptionTab === ExceptionHandlingSectionType.TIMEOUT) {
      return 'tkey;teams_call_queue.exception_handling.timeout.help_text';
    }

    if (this.activeExceptionTab === ExceptionHandlingSectionType.NO_AGENTS) {
      return 'tkey;teams_call_queue.exception_handling.no_agents.help_text';
    }

    return 'tkey;teams_call_queue.exception_handling.overflow.help_text';
  }

  setAudioSetting(update: ZiroFormsUpdate<UiAudioSettings>): void {
    this._audioSettings = update.new;
    this._greetingAudioChanged = !isEqual(
      this._audioSettings.greetingUpload,
      this._initialAudioSettings.greetingUpload,
    );
    this._musicOnHoldAudioChanged = !isEqual(
      this._audioSettings.musicOnHoldUpload,
      this._initialAudioSettings.musicOnHoldUpload,
    );
    if (!isEqual(update.new, update.old)) {
      this.entitySource.next({
        ...this.entity,
        audioSettings: this._audioSettings,
      });
    }
  }

  setCallbackSettings(update: ZiroFormsUpdate<UiCallbackSettings>): void {
    this._callbackSettings = update.new;
    this._offerAudioChanged = !isEqual(
      this._callbackSettings.offerAudioUpload,
      this._initialCallbackSettings.offerAudioUpload,
    );
    if (!isEqual(update.new, update.old)) {
      this.entitySource.next({
        ...this.entity,
        callbackSettings: this._callbackSettings,
      });
    }
  }

  onCallbackSettingsToggled(event: CollapsibleSectionCardSwitchValue): void {
    this.entitySource.next({
      ...this.entity,
      callbackSettings: {
        ...this.entity.callbackSettings,
        enabled: event.isChecked,
      },
    });
  }

  toEntity = (formData: UiCallQueue): UiCallQueue => {
    const isLongestIdle = formData.routingMethod === RoutingMethod.LONGEST_IDLE;
    const isPresenceAwareRouting = formData.presenceAwareRouting;

    if (isLongestIdle && isPresenceAwareRouting) {
      return {
        ...formData,
        presenceAwareRouting: false,
      };
    }

    return formData;
  };

  isResourceAccountDisabled(resourceAccount: MicrosoftTeamsUserResult): boolean {
    return (
      this.nonSelectableResourceAccountGuids()().includes(resourceAccount.guid) ||
      !resourceAccount.enterpriseVoiceEnabled ||
      !resourceAccount.lineUri
    );
  }

  isCallingIdResourceAccountDisabled(resourceAccount: MicrosoftTeamsUserResult): boolean {
    return (
      this.nonSelectableCallingIdResourceAccountGuids()().includes(resourceAccount.guid) ||
      !resourceAccount.enterpriseVoiceEnabled ||
      !resourceAccount.lineUri
    );
  }

  preventClickOnDisabledOptions($event: PointerEvent, resourceAccount: MicrosoftTeamsUserResult): void {
    if (this.isResourceAccountDisabled(resourceAccount)) {
      $event.stopPropagation();
    }
  }

  preventClickOnCallingIdDisabledOptions($event: PointerEvent, resourceAccount: MicrosoftTeamsUserResult): void {
    if (this.isCallingIdResourceAccountDisabled(resourceAccount)) {
      $event.stopPropagation();
    }
  }

  getOptionTooltip(resourceAccount: MicrosoftTeamsUserResult): string {
    if (this.nonSelectableResourceAccountGuids()().includes(resourceAccount.guid)) {
      const callQueueName = this.allTenantCallQueues().find((callQueue) =>
        callQueue.resourceAccountGuids.includes(resourceAccount.guid),
      ).name;

      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.already_assigned.tooltip',
        {
          voiceApp: callQueueName,
        },
      );
    } else if (!resourceAccount.enterpriseVoiceEnabled) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.enterprise_voice_disabled.tooltip',
      );
    } else if (!resourceAccount.lineUri) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.no_line_uri.tooltip',
      );
    }
  }

  getCallingIdOptionTooltip(resourceAccount: MicrosoftTeamsUserResult): string {
    if (this.nonSelectableCallingIdResourceAccountGuids()().includes(resourceAccount.guid)) {
      const callQueueName = this.allTenantCallQueues().find((callQueue) =>
        callQueue.callingIdResourceAccountGuids.includes(resourceAccount.guid),
      ).name;

      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.already_assigned.tooltip',
        {
          voiceApp: callQueueName,
        },
      );
    } else if (!resourceAccount.enterpriseVoiceEnabled) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.enterprise_voice_disabled.tooltip',
      );
    } else if (!resourceAccount.lineUri) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.no_line_uri.tooltip',
      );
    }
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _removeInvalidEntries(): Observable<void> {
    return new Observable((subscriber) => {
      const invalidResourceAccounts = this.callQueueView()()
        .resourceAccounts.filter((entry) => entry.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND)
        .map((entry) => entry.guid);
      const invalidCallingIdResourceAccounts = this.callQueueView()()
        .callingIdResourceAccounts.filter(
          (entry) => entry.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND,
        )
        .map((entry) => entry.guid);
      const resourceAccountGuids = this.entity.resourceAccountGuids.filter(
        (guid) => !invalidResourceAccounts.includes(guid),
      );
      const callingIdResourceAccountGuids = this.entity.callingIdResourceAccountGuids.filter(
        (guid) => !invalidCallingIdResourceAccounts.includes(guid),
      );

      this.entity = {
        ...this.entity,
        resourceAccountGuids,
        callingIdResourceAccountGuids,
      };
      this.entitySource.next(this.entity);

      this.agentsCard().removeInvalidAgents();

      subscriber.next();
    });
  }

  private _confirmRemoveInvalidEntries(): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'remove-invalid-entries-modal',
      modalViewProperties: {
        icon: ZiroIcons.WARNING_TRIANGLE,
        iconClass: 'text-warning',
        title: this._translateService.instant('tkey;teams_call_queues.list_view.invalid_modal.title'),
        promptBody: this._translateService.instant('tkey;teams_call_queues.list_view.invalid_modal.body'),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'invalid-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.confirm',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'invalid-modal-confirm-button',
            cb: () => this._removeInvalidEntries().pipe(tap(() => this._onValidQueueSave())),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _confirmDisableCallbacksOnConferenceModeOff(): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'disable-callbacks-modal',
      modalViewProperties: {
        icon: ZiroIcons.WARNING_TRIANGLE,
        iconClass: 'text-warning',
        title: this._translateService.instant('tkey;teams_call_queue.callbacks.disable_confirm_modal.title'),
        promptBody: this._translateService.instant('tkey;teams_call_queue.callbacks.disable_confirm_modal.body'),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'disable-callbacks-modal-cancel-button',
            cb: () => {
              this.entitySource.next({ ...this.entity, conferenceMode: true });
              return of(null);
            },
          },
          {
            label: 'tkey;dialogs.button.confirm',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'disable-callbacks-modal-confirm-button',
            cb: () => {
              this.entitySource.next({
                ...this.entity,
                callbackSettings: { ...this.entity.callbackSettings, enabled: false },
              });
              return of(null);
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _buildViewResourceAccount(userResult: MicrosoftTeamsUserResult): VoiceAppViewEntity {
    const {
      ref: { displayName },
      lineUri,
      lineUriExtension,
      userPrincipalName: upn,
      guid,
    } = userResult;

    return {
      type: VoiceAppViewEntityType.RESOURCE_ACCOUNT,
      description: '',
      displayName,
      lineUri,
      lineUriExtension,
      upn,
      guid,
      status: MicrosoftTeamsCallQueueViewEntityStatus.VALID,
    };
  }

  private _checkQueueValidity(): boolean {
    return (
      this.callQueueView()().resourceAccounts.every((resourceAccount) =>
        resourceAccount.status === MicrosoftTeamsCallQueueViewEntityStatus.VALID
          ? true
          : !this.entity.resourceAccountGuids.find((guid) => guid === resourceAccount.guid),
      ) &&
      this.callQueueView()().callingIdResourceAccounts.every((resourceAccount) =>
        resourceAccount.status === MicrosoftTeamsCallQueueViewEntityStatus.VALID
          ? true
          : !this.entity.callingIdResourceAccountGuids.find((guid) => guid === resourceAccount.guid),
      ) &&
      this.agentsCard().isAgentsValid()
    );
  }

  private _setPopoverWidth(buttonRef: ElementRef<HTMLDivElement>, popoverRef: ElementRef<HTMLDivElement>): void {
    if (buttonRef && popoverRef) {
      const borderPixels = 2;
      this._renderer2.setStyle(
        popoverRef.nativeElement,
        'min-width',
        `${buttonRef.nativeElement.offsetWidth - borderPixels}px`,
      );
      if (!this.tooltipStyles) {
        this.tooltipStyles = { '--tooltip-width': `${buttonRef.nativeElement.offsetWidth - borderPixels}px` };
      }
    }
  }

  private _buildQueueRequestBody(
    responses: UiAudioPostResponse[],
    promptAudioIds: { overflow: string; timeout: string; noAgent: string },
  ): MicrosoftTeamsCallQueue {
    let requestBody = cloneDeep(this.entity);

    delete requestBody.audioSettings.greetingUpload;
    delete requestBody.audioSettings.musicOnHoldUpload;

    const { offerPromptType, notificationTargetGroup } = requestBody.callbackSettings;
    const callbackSettingsRest: CallbackSettings = omit(requestBody.callbackSettings, [
      'offerPromptType',
      'offerAudioUpload',
      'notificationTargetGroup',
    ]);
    requestBody.overflowSettings = {
      overflowThreshold: Number(requestBody.uiOverflowSettings.overflowThreshold),
      exceptionHandling: mapUiToExceptionHandling(requestBody.uiOverflowSettings, promptAudioIds.overflow),
    };
    requestBody.timeoutSettings = {
      timeoutThresholdInSeconds:
        Number(requestBody.uiTimeoutSettings.timeoutMinutes) * 60 +
        Number(requestBody.uiTimeoutSettings.timeoutSeconds),
      exceptionHandling: mapUiToExceptionHandling(requestBody.uiTimeoutSettings, promptAudioIds.timeout),
    };
    requestBody.noAgentSettings = {
      noAgentApplyTo: requestBody.uiNoAgentSettings.noAgentApplyToNewCallsOnly
        ? NoAgentApplyTo.NEW_CALLS
        : NoAgentApplyTo.ALL_CALLS,
      exceptionHandling: mapUiToExceptionHandling(requestBody.uiNoAgentSettings, promptAudioIds.noAgent),
    };
    delete requestBody.uiOverflowSettings;
    delete requestBody.uiTimeoutSettings;
    delete requestBody.uiNoAgentSettings;

    requestBody = {
      ...requestBody,
      audioSettings: {
        ...requestBody.audioSettings,
        greetingAudioId:
          requestBody.audioSettings.greetingType !== GreetingType.CUSTOM_GREETING
            ? ''
            : requestBody.audioSettings.greetingAudioId,
        musicOnHoldAudioId:
          requestBody.audioSettings.musicOnHoldType !== MusicOnHoldType.CUSTOM_MUSIC
            ? ''
            : requestBody.audioSettings.musicOnHoldAudioId,
        greetingTtsMessage:
          requestBody.audioSettings.greetingType !== GreetingType.TTS_MESSAGE
            ? ''
            : requestBody.audioSettings.greetingTtsMessage,
      },
      callbackSettings: callbackSettingsRest.enabled
        ? {
            ...callbackSettingsRest,
            requestDtmf: callbackSettingsRest.requestDtmf || null,
            offerAudioPromptFileGuid:
              offerPromptType === OfferPromptType.AUDIO_FILE ? callbackSettingsRest.offerAudioPromptFileGuid : '',
            offerTtsPrompt: offerPromptType === OfferPromptType.TTS_MESSAGE ? callbackSettingsRest.offerTtsPrompt : '',
            notificationTargetGroupGuid: notificationTargetGroup?.value ?? '',
          }
        : {
            ...callbackSettingsRest,
            requestDtmf: null,
            waitTimeBeforeOfferInSeconds: null,
            numberOfCallsInQueueBeforeOffer: null,
            callToAgentRatioThresholdBeforeOffer: null,
            offerAudioPromptFileGuid: '',
            offerTtsPrompt: '',
            notificationTargetGroupGuid: '',
          },
    };

    responses.forEach((response) => {
      if (response.audioIdType === 'greetingAudioId') {
        requestBody = {
          ...requestBody,
          audioSettings: { ...requestBody.audioSettings, greetingAudioId: response.id },
        };
      } else if (response.audioIdType === 'musicOnHoldAudioId') {
        requestBody = {
          ...requestBody,
          audioSettings: { ...requestBody.audioSettings, musicOnHoldAudioId: response.id },
        };
      } else if (response.audioIdType === 'offerAudioPromptFileGuid') {
        requestBody = {
          ...requestBody,
          callbackSettings: { ...requestBody.callbackSettings, offerAudioPromptFileGuid: response.id },
        };
      }
    });

    return requestBody;
  }

  private _onSaveClick(): void {
    if (this.agentsCard().isChannelConfigurationUnresolved()) {
      this.isFormSubmitted = true;
      this.agentsCard().triggerSubmitValidation();
      this._bottomNavService.setBottomNavValidationError(true);
      return;
    }

    if (!this._validateFormAndShowErrors()) {
      return;
    }

    if (this._checkQueueValidity()) {
      this._onValidQueueSave();
    } else {
      this._confirmRemoveInvalidEntries();
    }
  }

  private _validateFormAndShowErrors(): boolean {
    this.isFormSubmitted = true;
    this.agentsCard().triggerSubmitValidation();
    this.audioFormComponent().triggerSubmitValidation();
    this.callbackSettingsFormComponent().triggerSubmitValidation();

    const isOverflowFormValid = this.overflowFormComponent().validateAndShowErrors();
    const isTimeoutFormValid = this.timeoutFormComponent().validateAndShowErrors();
    const isNoAgentsFormValid = this.noAgentsFormComponent().validateAndShowErrors();

    this._setExceptionTabValidationState(ExceptionHandlingSectionType.OVERFLOW, isOverflowFormValid);
    this._setExceptionTabValidationState(ExceptionHandlingSectionType.TIMEOUT, isTimeoutFormValid);
    this._setExceptionTabValidationState(ExceptionHandlingSectionType.NO_AGENTS, isNoAgentsFormValid);

    const firstInvalidExceptionTab = [
      ExceptionHandlingSectionType.OVERFLOW,
      ExceptionHandlingSectionType.TIMEOUT,
      ExceptionHandlingSectionType.NO_AGENTS,
    ].find((tab) => this.exceptionTabsValidationState[tab]() === ZiroFormsValidationState.INVALID);
    if (firstInvalidExceptionTab) {
      this.activeExceptionTab = firstInvalidExceptionTab;
    }

    const isValid =
      this.isFormValid() &&
      this.agentsCard().isFormValid() &&
      this.audioFormComponent().isFormValid() &&
      this.callbackSettingsFormComponent().isFormValid() &&
      isOverflowFormValid &&
      isTimeoutFormValid &&
      isNoAgentsFormValid;

    this._bottomNavService.setBottomNavValidationError(!isValid);

    return isValid;
  }

  private _onValidQueueSave(): void {
    this._validateAndSubmitSource.next(true);
    this.isSubmitting = true;
    this._setPending('save-button', true);
    this._setDisabled('delete-button', true);
    this._setDisabled('cancel-button', true);

    const pendingUploads: { file: File; audioIdType: UiAudioPostResponse['audioIdType'] }[] = [];

    if (
      this._audioSettings.greetingType === GreetingType.CUSTOM_GREETING &&
      this._audioSettings.greetingUpload &&
      this._greetingAudioChanged
    ) {
      pendingUploads.push({ file: this._audioSettings.greetingUpload, audioIdType: 'greetingAudioId' });
    }

    if (
      this._audioSettings.musicOnHoldType === MusicOnHoldType.CUSTOM_MUSIC &&
      this._audioSettings.musicOnHoldUpload &&
      this._musicOnHoldAudioChanged
    ) {
      pendingUploads.push({ file: this._audioSettings.musicOnHoldUpload, audioIdType: 'musicOnHoldAudioId' });
    }

    if (
      this._callbackSettings.offerPromptType === OfferPromptType.AUDIO_FILE &&
      this._callbackSettings.offerAudioUpload &&
      this._offerAudioChanged
    ) {
      pendingUploads.push({ file: this._callbackSettings.offerAudioUpload, audioIdType: 'offerAudioPromptFileGuid' });
    }

    const audioPostObs: Observable<UiAudioPostResponse[]> = pendingUploads.length
      ? combineLatest(
          pendingUploads.map((upload) =>
            this._audioFileResource
              .createAudioFile(this.entity.tenantId.toString(), 'call-queues', upload.file)
              .pipe(map((response: AudioPostResponse) => ({ ...response, audioIdType: upload.audioIdType }))),
          ),
        )
      : of([]);

    const promptAudioIdsObs = this._buildPromptAudioUploadObs();

    if (!this._callQueueId) {
      combineLatest([audioPostObs, promptAudioIdsObs])
        .pipe(
          switchMap(([response, promptAudioIds]) => {
            const body = this._buildQueueRequestBody(response, promptAudioIds);
            return this._callQueueContext.createCallQueue(body);
          }),
          switchMap(() => (this.is360ViewChild ? this._ms360ViewContext.refresh(this._upn) : of(null))),
          catchError((error) => {
            this.isSubmitting = false;
            this._setPending('save-button', false);
            this._setDisabled('delete-button', false);
            this._setDisabled('cancel-button', false);
            return throwError(() => error);
          }),
        )
        .subscribe(() => {
          this._toastService.pushSaveToast(
            'tkey;teams_call_queues.list_view.toast.service_type',
            this.entity.name,
            ZiroIcons.CALL_QUEUE,
          );
          this._navigateAfterAction();
        });
    } else {
      combineLatest([audioPostObs, promptAudioIdsObs])
        .pipe(
          switchMap(([response, promptAudioIds]) => {
            const body = this._buildQueueRequestBody(response, promptAudioIds);
            return this._callQueueContext.updateCallQueue(body);
          }),
          switchMap(() => (this.is360ViewChild ? this._ms360ViewContext.refresh(this._upn) : of(null))),
          catchError((error) => {
            this.isSubmitting = false;
            this._setPending('save-button', false);
            this._setDisabled('delete-button', false);
            this._setDisabled('cancel-button', false);
            return throwError(() => error);
          }),
        )
        .subscribe(() => {
          this._toastService.pushSaveToast(
            'tkey;teams_call_queues.list_view.toast.service_type',
            this.entity.name,
            ZiroIcons.CALL_QUEUE,
          );
          this._navigateAfterAction();
        });
    }
  }

  private _setExceptionTabValidationState(tab: ExceptionHandlingSectionType, isValid: boolean): void {
    this.exceptionTabsValidationState[tab].set(
      isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    );
  }

  private _buildPromptAudioUploadObs(): Observable<{ overflow: string; timeout: string; noAgent: string }> {
    const uploadOrKeepExisting = (
      uiSettings: UiExceptionHandlingSettings,
      initialSettings: UiExceptionHandlingSettings,
    ): Observable<string> => {
      const hasNewAudioPrompt =
        uiSettings.promptType === ExceptionHandlingPromptType.AUDIO_FILE &&
        !!uiSettings.audioPromptUpload &&
        uiSettings.audioPromptUpload !== initialSettings?.audioPromptUpload;

      return hasNewAudioPrompt
        ? this._audioFileResource
            .createAudioFile(this.entity.tenantId.toString(), 'call-queues', uiSettings.audioPromptUpload)
            .pipe(map((uploadResponse: AudioPostResponse) => uploadResponse.id))
        : of(uiSettings.audioPromptFileGuid ?? '');
    };

    return forkJoin({
      overflow: uploadOrKeepExisting(this.entity.uiOverflowSettings, this._initialExceptionSettings.uiOverflowSettings),
      timeout: uploadOrKeepExisting(this.entity.uiTimeoutSettings, this._initialExceptionSettings.uiTimeoutSettings),
      noAgent: uploadOrKeepExisting(this.entity.uiNoAgentSettings, this._initialExceptionSettings.uiNoAgentSettings),
    });
  }

  private _onDeleteClick(): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;teams_call_queues.list_view.delete_modal.title'),
        promptBody: this._translateService.instant('tkey;teams_call_queues.list_view.delete_modal.body', {
          name: this.entity.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._onDelete();
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onDelete(): Observable<Microsoft360View> {
    this._setPending('delete-button', true);
    this._setDisabled('save-button', true);
    this._setDisabled('cancel-button', true);
    return this._callQueueContext.deleteCallQueue(this.entity.callQueueId).pipe(
      switchMap(() => (this.is360ViewChild ? this._ms360ViewContext.refresh(this._upn) : of(null))),
      tap(() => {
        this._ziroFormStateService.setIsFormDirty(false);
        this._toastService.pushDeleteToast('tkey;teams_call_queues.list_view.toast.service_type', this.entity.name);
        this._navigateAfterAction();
      }),
    );
  }

  private _setPending(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _setDisabled(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _navigateAfterAction(): void {
    if (this._returnUrl) {
      this._router.navigateByUrl(this._returnUrl);
    } else {
      const navigationFragment = !this.is360ViewChild ? '../' : '../..';
      this._router.navigate([navigationFragment], { relativeTo: this._route });
    }
  }

  protected readonly MicrosoftTeamsCallQueueViewEntityStatus = MicrosoftTeamsCallQueueViewEntityStatus;
  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ExceptionHandlingSectionType = ExceptionHandlingSectionType;
  protected readonly ZiroFormsValidationState = ZiroFormsValidationState;
}
